/**
 * Друкована форма стандартних звітів (ч.1036): та сама вибірка, простий
 * бланк (шапка організації, назва, період, таблиця, підсумок). Відкривається
 * у новій вкладці з кнопок «друк» звітів; body.print-mode ховає решту
 * застосунку (той самий механізм, що акт звірки).
 */
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { reports, type LedgerBalances } from '@/api/documents';
import { DOC_TABLE_NAMES_UK } from '@/utils/moduleNames';
import { money, qty, signedBalance } from './ledgerModel';
import { periodLabel } from './ledgerColumns';

type Kind = 'tb' | 'tba' | 'card' | 'sa' | 'an' | 'to' | 'gl' | 'chess';
const BAL_HEAD = ['Сальдо поч. Дт', 'Сальдо поч. Кт', 'Оборот Дт', 'Оборот Кт', 'Сальдо кін. Дт', 'Сальдо кін. Кт'];
const QTY_HEAD = ['Кіл. поч.', 'Кіл. Дт', 'Кіл. Кт', 'Кіл. кін.'];
const bal = (b: LedgerBalances) => [b.opening_dt, b.opening_cr, b.turnover_dt, b.turnover_cr, b.closing_dt, b.closing_cr];
const qcols = (b: LedgerBalances) => [b.opening_qty, b.turnover_qty_dt, b.turnover_qty_cr, b.closing_qty].map((v) => qty(v));

export default function LedgerPrintPage() {
  const [sp] = useSearchParams();
  const kind = (sp.get('kind') ?? 'tb') as Kind;
  const g = (k: string) => sp.get(k) ?? '';
  const base = { organization_id: g('org'), date_from: g('from'), date_to: g('to') };

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = orgsQ.data?.items.find((o) => o.id === base.organization_id)?.name ?? '';

  const q = useQuery({
    queryKey: ['ledger-print', kind, [...sp.entries()].join('&')],
    queryFn: async () => {
      if (kind === 'tb') return { kind, data: await reports.trialBalance({ ...base, expanded: g('expanded') === '1', with_quantity: g('qty') === '1' }) };
      if (kind === 'tba') return { kind, data: await reports.trialBalanceAccount({ ...base, account_code: g('account'), group_by: g('group') || 'subconto1', with_quantity: g('qty') === '1' }) };
      if (kind === 'card') return { kind, data: await reports.accountCard({ ...base, account_code: g('account') || undefined, subconto_id: g('sc') || undefined, corr_account_code: g('corr') || undefined, side: (g('side') === 'DT' || g('side') === 'CR') ? (g('side') as 'DT' | 'CR') : undefined }) };
      if (kind === 'an') return { kind, data: await reports.accountAnalysis({ ...base, account_code: g('account') }) };
      if (kind === 'to') return { kind, data: await reports.accountTurnover({ ...base, account_code: g('account'), periodicity: g('per') || 'month' }) };
      if (kind === 'gl') return { kind, data: await reports.generalLedger({ ...base, periodicity: g('per') || 'month' }) };
      if (kind === 'chess') return { kind, data: await reports.chessSheet({ ...base, level: g('level') || 'leaf' }) };
      return { kind: 'sa' as const, data: await reports.subcontoAnalysis({ ...base, subconto_type: g('type'), subconto_id: g('id') || undefined }) };
    },
    enabled: !!base.organization_id && !!base.date_from,
  });

  let title = '';
  let head: string[] = [];
  let body: { cells: (string | number)[]; level?: number; bold?: boolean }[] = [];
  let numFrom = 1;   // з якої колонки — числа (вирівнювання праворуч)
  const r = q.data;
  if (r?.kind === 'tb') {
    const wq = !!r.data.with_quantity;
    title = 'Оборотно-сальдова відомість' + (r.data.expanded ? ' (розгорнуте сальдо)' : '');
    head = ['Рахунок', ...BAL_HEAD, ...(wq ? QTY_HEAD : [])];
    body = [
      ...r.data.rows.map((x) => ({ cells: [`${x.account_code} ${x.account_name}`, ...bal(x).map((v) => money(v)), ...(wq ? qcols(x) : [])], level: x.level, bold: x.is_group })),
      { cells: ['Разом', ...bal(r.data.totals).map((v) => money(v, { zero: '0,00' })), ...(wq ? qcols(r.data.totals) : [])], bold: true },
    ];
  } else if (r?.kind === 'tba') {
    const wq = !!r.data.with_quantity;
    title = `ОСВ по рахунку ${r.data.account_code} ${r.data.account_name}`;
    head = ['Субконто', ...BAL_HEAD, ...(wq ? QTY_HEAD : [])];
    body = [
      ...r.data.rows.map((x) => ({ cells: [x.label, ...bal(x).map((v) => money(v)), ...(wq ? qcols(x) : [])], level: x.level, bold: x.is_group })),
      { cells: ['Разом', ...bal(r.data.totals).map((v) => money(v, { zero: '0,00' })), ...(wq ? qcols(r.data.totals) : [])], bold: true },
    ];
  } else if (r?.kind === 'card') {
    title = (r.data.account_code ? `Картка рахунку ${r.data.account_code} ${r.data.account_name}` : `Картка субконто`) + (g('scName') ? ` · ${g('scName')}` : '') + (g('corr') ? ` · кор. ${g('corr')}` : '') + (g('side') ? (g('side') === 'DT' ? ' · лише дебет' : ' · лише кредит') : '');
    const hq = r.data.rows.some((x) => x.quantity != null && Number(x.quantity) !== 0);
    head = ['Дата', 'Документ', 'Аналітика', 'Кор. рах.', 'Зміст', 'Дебет', 'Кредит', ...(hq ? ['Кіл.'] : []), 'Сальдо'];
    const t = r.data.totals;
    const sb = (v: string | number) => { const b = signedBalance(v); return b.side === 'cr' ? `−${b.text}` : b.text; };
    body = [
      { cells: ['', 'Сальдо на початок', '', '', '', '', '', ...(hq ? [''] : []), sb(Number(t.opening_dt) - Number(t.opening_cr))], bold: true },
      ...r.data.rows.map((x) => ({ cells: [
        new Date(x.entry_date).toLocaleDateString('uk-UA'),
        `${x.document_type ? (DOC_TABLE_NAMES_UK[x.document_type] ?? x.document_type) : ''}${x.document_number ? ` № ${x.document_number}` : ''}`,
        x.analytics, x.corr_account_code, x.content ?? '', money(x.amount_dt), money(x.amount_cr), ...(hq ? [qty(x.quantity)] : []), sb(x.running_balance),
      ] })),
      { cells: ['', 'Обороти і сальдо на кінець', '', '', '', money(t.turnover_dt, { zero: '0,00' }), money(t.turnover_cr, { zero: '0,00' }), ...(hq ? [''] : []), sb(Number(t.closing_dt) - Number(t.closing_cr))], bold: true },
    ];
  } else if (r?.kind === 'sa') {
    title = `Аналіз субконто: ${r.data.subconto_label}` + (g('name') ? ` · ${g('name')}` : '');
    head = ['Субконто / рахунок', ...BAL_HEAD];
    body = [
      ...r.data.rows.map((x) => ({ cells: [x.label, ...bal(x).map((v) => money(v))], level: x.level, bold: x.is_group })),
      { cells: ['Разом', ...bal(r.data.totals).map((v) => money(v, { zero: '0,00' }))], bold: true },
    ];
  } else if (r?.kind === 'an') {
    const t = r.data.totals;
    title = `Аналіз рахунку ${r.data.account_code} ${r.data.account_name}`;
    head = ['Кореспондуючий рахунок', 'З кредиту в дебет', 'З дебету в кредит'];
    body = [
      { cells: [`Сальдо на початок: Дт ${money(t.opening_dt, { zero: '0,00' })} · Кт ${money(t.opening_cr, { zero: '0,00' })}`, '', ''], bold: true },
      ...r.data.rows.map((x) => ({ cells: [`${x.corr_account_code} ${x.corr_account_name ?? ''}`, money(x.turnover_dt), money(x.turnover_cr)] })),
      { cells: ['Разом', money(t.turnover_dt, { zero: '0,00' }), money(t.turnover_cr, { zero: '0,00' })], bold: true },
      { cells: [`Сальдо на кінець: Дт ${money(t.closing_dt, { zero: '0,00' })} · Кт ${money(t.closing_cr, { zero: '0,00' })}`, '', ''], bold: true },
    ];
  } else if (r?.kind === 'to') {
    title = `Обороти рахунку ${r.data.account_code} ${r.data.account_name}`;
    head = ['Період / кор. рахунок', ...BAL_HEAD];
    body = [
      ...r.data.rows.map((x) => ({ cells: [x.label, ...bal(x).map((v) => money(v))], level: x.level, bold: x.kind === 'period' })),
      { cells: ['Разом', ...bal(r.data.totals).map((v) => money(v, { zero: '0,00' }))], bold: true },
    ];
  } else if (r?.kind === 'gl') {
    title = 'Головна книга';
    head = ['Рахунок / період / кор. рахунок', ...BAL_HEAD];
    body = r.data.rows.map((x) => ({ cells: [x.label, ...bal(x).map((v) => money(v))], level: x.level, bold: x.kind === 'account' }));
  } else if (r?.kind === 'chess') {
    title = 'Шахматна відомість' + (r.data.level === 'synthetic' ? ' (синтетичні рахунки)' : '');
    const look = new Map(r.data.cells.map((c) => [`${c.dt}|${c.cr}`, c.amount]));
    head = ['Дт \\ Кт', ...r.data.cr_accounts.map((c) => c.code), 'Разом'];
    body = [
      ...r.data.dt_accounts.map((d) => ({ cells: [d.code, ...r.data.cr_accounts.map((c) => money(look.get(`${d.code}|${c.code}`) ?? 0)), money(r.data.row_totals[d.code])] })),
      { cells: ['Разом', ...r.data.cr_accounts.map((c) => money(r.data.col_totals[c.code])), money(r.data.total, { zero: '0,00' })], bold: true },
    ];
  }
  if (kind === 'card') numFrom = 5;

  return (
    <div className="print-page ledger-print">
      <div className="print-hide ledger-print__bar">
        <button type="button" className="primary" onClick={() => window.print()}>друкувати</button>
        <button type="button" onClick={() => (window.history.length > 1 ? window.history.back() : window.close())}>закрити</button>
      </div>
      {q.isError && <p>Не вдалося завантажити звіт.</p>}
      {q.isLoading && <p>Завантаження…</p>}
      {r && (
        <>
          <div className="ledger-print__org">{orgName}</div>
          <h2 className="ledger-print__title">{title}</h2>
          <div className="ledger-print__period">{periodLabel(base.date_from, base.date_to)}</div>
          <table className="ledger-print__table">
            <thead><tr>{head.map((h, i) => <th key={i} className={i >= numFrom ? 'num' : ''}>{h}</th>)}</tr></thead>
            <tbody>
              {body.map((row, i) => (
                <tr key={i} className={row.bold ? 'bold' : ''}>
                  {row.cells.map((c, j) => (
                    <td key={j} className={j >= numFrom ? 'num' : ''}
                      style={j === 0 && row.level ? { paddingLeft: `${row.level * 1.2}rem` } : undefined}>{c}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="ledger-print__sign">Головний бухгалтер ____________________</div>
        </>
      )}
    </div>
  );
}
